import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Heart, MessageCircle, Share2, Bookmark, Repeat, X } from 'lucide-react';
import { postService, commentService } from '@/services/api';
import { UserAvatar } from '@/components/ui/Avatar';
import { formatRelativeDate, cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { ROUTES } from '@/constants';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';

export default function PostPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { user } = useAuth();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [isReposted, setIsReposted] = useState<boolean>(false);
  const [repostCount, setRepostCount] = useState<number>(0);
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = async () => {
    if (!id) return;
    setLoading(true);
    const { data } = await postService.getPostById(id);
    if (data) {
      setPost(data);
      setIsLiked(!!data.likes?.some((l: any) => l.user_id === user?.id));
      setLikeCount(data.likes?.length || 0);
      setIsReposted(!!data.reposts?.some((r: any) => r.user_id === user?.id));
      setRepostCount(data.reposts?.length || 0);
      setIsBookmarked(!!data.bookmarks?.some((b: any) => b.user_id === user?.id));
    }
    // Load comments
    const { data: commentsData } = await commentService.getComments(id!);
    setComments(commentsData || []);
    setLoading(false);
  };

  const handleLike = async () => {
    if (!post) return;
    const prevLiked = isLiked;
    const prevCount = likeCount;
    setIsLiked(!prevLiked);
    setLikeCount((c) => (prevLiked ? Math.max(0, c - 1) : c + 1));

    try {
      const { error } = prevLiked
        ? await postService.unlikePost(id!)
        : await postService.likePost(id!);
      if (error) {
        setIsLiked(prevLiked);
        setLikeCount(prevCount);
        showError(error.message || 'Failed to update like.');
      }
    } catch (err: any) {
      setIsLiked(prevLiked);
      setLikeCount(prevCount);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleRepost = async () => {
    if (!post) return;
    const prevReposted = isReposted;
    const prevCount = repostCount;
    setIsReposted(!prevReposted);
    setRepostCount((c) => (prevReposted ? Math.max(0, c - 1) : c + 1));

    try {
      const { error } = prevReposted
        ? await postService.unrepostPost(id!)
        : await postService.repostPost(id!);
      if (error) {
        setIsReposted(prevReposted);
        setRepostCount(prevCount);
        showError(error.message || 'Failed to update repost.');
      }
    } catch (err: any) {
      setIsReposted(prevReposted);
      setRepostCount(prevCount);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleBookmark = async () => {
    if (!post) return;
    const prevBookmarked = isBookmarked;
    setIsBookmarked(!prevBookmarked);

    try {
      const { error } = prevBookmarked
        ? await postService.unbookmarkPost(id!)
        : await postService.bookmarkPost(id!);
      if (error) {
        setIsBookmarked(prevBookmarked);
        showError(error.message || 'Failed to update bookmark.');
      }
    } catch (err: any) {
      setIsBookmarked(prevBookmarked);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentContent.trim() || !post) return;
    if (!user) {
      showError('Please sign in to comment.');
      return;
    }

    try {
      const { error } = await commentService.createComment(id!, commentContent);
      if (error) {
        console.error('Comment error:', error);
        showError(error.message || 'Failed to post comment. Please try again.');
        return;
      }
      setCommentContent('');
      loadPost();
    } catch (err: any) {
      console.error('Comment exception:', err);
      showError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const closeError = () => setError(null);

  const showError = (message: string) => {
    setError(message);
    toast('Failed', message, 'destructive');
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><div className="skeleton-text h-4 w-32" /></div>;
  if (!post) return <div className="flex h-64 items-center justify-center"><p className="text-neutral-500 dark:text-neutral-400">Post not found</p></div>;

  return (
    <div className="mx-auto max-w-2xl px-4 pt-4">
      <div className={cn(
        // Mobile: bottom border only, padding inside, no corner radius, no background block, full bleed.
        'border-b border-neutral-200 dark:border-neutral-800 p-3',
        // Desktop: full post card with padding, rounded corners, full border, no background block.
        'sm:post-card sm:p-4 sm:bg-transparent sm:dark:bg-transparent',
      )}>
        <div className="flex gap-3">
          <UserAvatar src={post.profiles?.avatar_url} size="md" />
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-semibold">{post.profiles?.display_name || post.profiles?.username}</span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">@{post.profiles?.username}</span>
              <span className="text-xs text-neutral-400 dark:text-neutral-500">· {formatRelativeDate(post.created_at)}</span>
            </div>
            <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{post.content}</div>
            {post.post_media && post.post_media.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {post.post_media.map((m: any, i: number) => (
                  <img key={i} src={m.url} alt="Media" className="aspect-square w-full rounded-xl object-cover" />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <button
            onClick={handleLike}
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isLiked ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-600 dark:text-neutral-300',
            )}
            aria-label={isLiked ? 'Unlike' : 'Like'}
          >
            <Heart size={18} className={cn(isLiked && 'fill-current')} />
            <span>{likeCount}</span>
          </button>
          <button
            onClick={() => navigate(ROUTES.POST.replace(':id', id!))}
            className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
            aria-label="Comments"
          >
            <MessageCircle size={18} />
            <span>{post.comments_count || 0}</span>
          </button>
          <button
            onClick={handleRepost}
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isReposted ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-600 dark:text-neutral-300',
            )}
            aria-label={isReposted ? 'Undo repost' : 'Repost'}
          >
            <Repeat size={18} />
            <span>{repostCount}</span>
          </button>
          <button
            onClick={handleBookmark}
            className={cn(
              'flex items-center rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isBookmarked ? 'text-brand-600 dark:text-brand-400' : 'text-neutral-600 dark:text-neutral-300',
            )}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
          >
            <Bookmark size={18} className={cn(isBookmarked && 'fill-current')} />
          </button>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-3 text-lg font-semibold">Comments</h3>
        <form onSubmit={handleCommentSubmit} className="flex items-center gap-3">
          <UserAvatar size="md" className="shrink-0" />
          <input
            type="text"
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            placeholder={!user ? 'Sign in to comment' : 'Add a comment...'}
            disabled={!user}
            className="min-w-0 flex-1 bg-transparent text-sm focus:ring-0 placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
          />
          <button
            type="submit"
            disabled={!commentContent.trim() || !user}
            className="shrink-0 text-sm font-medium text-brand-600 dark:text-brand-400"
          >
            Post
          </button>
        </form>

        {comments.length === 0 && (
          <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
            No comments yet. Be the first to comment!
          </p>
        )}
        {comments.map((comment: any) => (
          <div key={comment.id} className="mt-4 flex gap-3">
            <UserAvatar src={comment.profiles?.avatar_url} size="sm" className="shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1 text-sm">
                <span className="font-medium">{comment.profiles?.display_name || comment.profiles?.username}</span>
                <span className="text-neutral-400 dark:text-neutral-500">· {formatRelativeDate(comment.created_at)}</span>
              </div>
              <div className="break-words text-sm">{comment.content}</div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <Modal isOpen={!!error} onClose={closeError}>
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            Something went wrong
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">{error}</p>
          <button
            onClick={closeError}
            className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <X size={16} className="mr-2" />
            Dismiss
          </button>
        </Modal>
      )}
    </div>
  );
}
